import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import styled from 'styled-components';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE
} from '../../../constants/commonConstants';
import { convertDateInputToEpoch } from '../../../../util/timezone';
import {
  TransactionDetailsTable,
  TransactionDetalisPanel
} from '../../../common/TransactionDetalis/styled';
import {
  genarateTableConfigs,
  generateGroupedTableConfigs,
  getGlobalSettings
} from '../../../common/utils';
import ActivityInfoFilters from './ActivityInfoFilters';
import { DEFAULT_MAP_NAMESPACE } from '../../../common/TransactionDetalis/constants';
import ComponentsMap from '../../../common/ComponentsMap';

import { isEmpty, result } from '../../../../util/lodashlike';

const MIN_SEARCH_LENGTH = 3;

// FIN-12076 — title-case a raw custom_data key for use as a column header when
// no configured label exists. Splits on underscores and capitalises each word
// so keys like `amount` / `currency` / `category` render as
// `Amount` / `Currency` / `Category` (and `mobile_wallet` → `Mobile Wallet`).
const titleCase = (key) =>
  String(key)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// FIN-11542 Phase-C Defect-2 — mirror the Tx tab's buildSearchParams
// (TransactionDetails.js:63): emit the typed term keyed by the chosen search
// field (`params[searchField] = searchTerm`) once it clears MIN_SEARCH_LENGTH.
// The previous code shipped `searchField`/`searchTerm` as separate params that
// the BE never read; this matches the Tx-tab shape used everywhere else.
const buildSearchParams = (query) => {
  const params = {};
  const term = query.searchTerm;
  if (query.searchField && term && term.length >= MIN_SEARCH_LENGTH) {
    params[query.searchField] = term;
  }
  return params;
};

// FIN-11542 Phase-C Defect-3 — flatten the Advanced Filter facet selections
// (Activity Type / Status / Channel) into the request. The facets are keyed by
// ACTIVITY_FILTER_FIELD_DEFS (activityType / activityStatus / channel) and the
// drawer stores them under `query.advancedFilters`. Spread them flat + carry
// the sort param, mirroring the Tx tab's buildAdvancedFilterParams.
// Exported for FIN-11969 unit coverage — this is the transform that makes the
// Activity tab's Advanced Filters actually reach the fetch payload.
export const buildAdvancedFilterParams = (query) => {
  const params = {};
  const af = query.advancedFilters || {};
  Object.keys(af).forEach((key) => {
    if (af[key]) params[key] = af[key];
  });
  if (query.sortOrder) params.sortOrder = query.sortOrder;
  return params;
};

const ActivityInformation = ({
  apiActions,
  alertId,
  isLoading,
  typologies,
  customerId,
  hasDownloadButton = true,
  showWidgetActionLabels = false,
  className,
  alertDetails = {},
  mapNamespace = DEFAULT_MAP_NAMESPACE,
  settings,
  gsQuery,
  hasTypologiesFilter = true,
  manualCaseDate,
  activityInfoData,
  hasTriggeredOnlySwitch = true,
  requireTriggeredOnlyParam,
  isSmartAssessment,
  transactionTriggeredVariants,
  isPrevention,
  queryFields,
  onDownloadComplete,
  isDownloadingActivity = false,
  // FIN-11542 — alertSourceId for variant facet gating in ActivityInfoFilters
  alertSourceId,
  // FIN-11398 — FRAML customer-detail mounts fetch by the customerId own-prop
  // (all of the customer's alerts, comma-joined alertId list), so the
  // single-alert `alertId === alertInfo.alertId` guard must not gate them.
  isCustomerScope = false,
  // FIN-13392 — async-download module tag. Defaults to the historical 'tm-activity' so
  // every other caller (alert-detail Smart Assessment, CM alert/case tabs) is undisturbed.
  // The FRAML customer-detail page overrides this to a dedicated module (mirrors how
  // TransactionDetails.js takes an explicit `module` prop for the same reason) so its
  // customer-scoped export stops being misfiled under TM_ACTIVITY.
  module = 'tm-activity'
}) => {
  const { data: alertInfo } = alertDetails;
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState({
    isTriggeredOnly: true,
    typologyId: 'all',
    variantId: undefined,
    duration: 30
  });

  const {
    widgetConfig,
    isSingleLevel = false,
    isVisible
  } = getGlobalSettings({ settings, gsQuery }) || {};

  const alertDateTO = useMemo(
    () =>
      manualCaseDate
        ? moment(new Date(manualCaseDate), 'DD/MM/YYYY')
        : alertInfo?.alertDate
        ? moment(new Date(alertInfo.alertDate), 'DD/MM/YYYY')
        : moment(),
    [manualCaseDate, alertInfo]
  );
  const alertDateFROM = useMemo(
    () =>
      moment(alertDateTO)
        .subtract(query.duration ?? 30, 'days')
        .add(1, 'days'),
    [alertDateTO]
  );

  const {
    getAMLSTMCommonActivityInfo,
    downloadAMLSTMCommonActivityInfo,
    getAMLSTMCommonTransactionTriggeredTypologies,
    getAMLSTMCommonTransactionTriggeredVariants,
    getAMLSTMCommonQueryFields
  } = apiActions;

  useEffect(() => {
    //The "To" date should not greater than alert Date.
    //So compare the "To" with "Alert Date" when date range only selected(without duration)
    if (query.from && query.to && !query.duration) {
      if (alertDateTO.endOf('day').isBefore(query.to.endOf('day'))) {
        setQuery((value) => ({
          ...value,
          from: alertDateFROM,
          to: alertDateTO,
          duration: 30
        }));
      }
    } else {
      setQuery((value) => ({
        ...value,
        from: alertDateFROM,
        to: alertDateTO,
        duration: value.duration
      }));
    }
  }, [alertDateTO]);

  useEffect(() => {
    // Set the typologyId as 'all' when the alert doesn't contain the selected typology while switching alert.
    if (query?.typologyId !== 'all') {
      if (
        (!isPrevention &&
          typologies?.length &&
          !typologies?.some(({ id }) => id === query?.typologyId)) ||
        (transactionTriggeredVariants?.length &&
          !transactionTriggeredVariants?.some((id) => id === query?.typologyId))
      ) {
        setQuery((value) => ({
          ...value,
          typologyId: 'all'
        }));
      }
    }
  }, [typologies, transactionTriggeredVariants, query?.typologyId]);

  useEffect(() => {
    const { from, to, typologyId, isTriggeredOnly } = query;
    if (
      isVisible &&
      from &&
      (manualCaseDate || isCustomerScope || alertId === alertInfo?.alertId)
    ) {
      // FIN-11542 Drift-2 — `triggeredOnly || requireTriggeredOnlyParam` was
      // losing the explicit `false` from the user toggle whenever
      // requireTriggeredOnlyParam was falsy/undefined (axios then dropped the
      // param and BE defaulted back to triggered-only). Explicit precedence:
      // BE-mandated true wins; otherwise pass the user's actual toggle.
      let triggeredOnly = hasTriggeredOnlySwitch ? isTriggeredOnly : undefined;
      const wireTriggeredOnly = requireTriggeredOnlyParam
        ? true
        : triggeredOnly;

      // FIN-11542 Phase-C Defect-2/3/4 — mirror the Tx tab: search term keyed
      // by field + flattened facet params + sortOrder reach the fetch payload.
      const searchParams = buildSearchParams(query);
      const advancedFilterParams = buildAdvancedFilterParams(query);

      getAMLSTMCommonActivityInfo({
        customerId,
        alertId,
        limit: currentPageSize,
        // Filter/sort/search changes reset to page 1 (onChangeWidgetAction +
        // SearchBar/Advanced/Sort handlers all call setCurrentPage). Page
        // navigation itself goes through onPaginationChange below.
        offset: (DEFAULT_CURRENT_PAGE - 1) * currentPageSize,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly: wireTriggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        // FIN-12079 — variantId is a FRAML-P Advanced-Filter facet, stored under
        // query.advancedFilters.variantId and already flattened into the payload by
        // buildAdvancedFilterParams. The old flat `query.variantId` read was dead
        // (Activity never sets a top-level variantId) so the applied Variant never
        // reached the request / persisted download filter_params. Rely on advancedFilterParams.
        ...searchParams,
        ...advancedFilterParams,
        isSmartAssessment
      });
    }
  }, [customerId, query, isVisible, alertInfo]);

  useEffect(() => {
    if (hasTypologiesFilter && alertId) {
      if (isPrevention) {
        getAMLSTMCommonTransactionTriggeredVariants({
          alertId,
          eventType: 'activity'
        });
      } else {
        getAMLSTMCommonTransactionTriggeredTypologies({ alertId });
      }
    }
    getAMLSTMCommonQueryFields();
  }, [alertId, hasTypologiesFilter]);

  const onChangeWidgetAction = (values) => {
    const triggeredOnly = hasTriggeredOnlySwitch
      ? values.isTriggeredOnly
      : undefined;

    // FIN-11542 Drift-2 — keep explicit user toggle (false) intact when
    // requireTriggeredOnlyParam is falsy. See useEffect comment above.
    setQuery({
      ...values,
      isTriggeredOnly: requireTriggeredOnlyParam ? true : triggeredOnly
    });
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  // FIN-11542 Phase-C Defect-2/3/4 — search / advanced-filter / sort go through
  // dedicated functional-merge handlers (mirrors the Tx tab's onSearchChange /
  // onAdvancedFiltersApply). The legacy `onChangeWidgetAction` path spread a
  // STALE `filters` closure (the TransactionSearchBar debounce captures the
  // initial query, before from/to are set), which dropped the from/to guard
  // and suppressed the refetch. Merging onto `prev` keeps fresh from/to intact.
  const onSearchChange = ({ searchField, searchTerm }) => {
    setQuery((prev) => ({ ...prev, searchField, searchTerm }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const onAdvancedFiltersChange = (advancedFilters) => {
    setQuery((prev) => ({ ...prev, advancedFilters: advancedFilters || {} }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const onSortChange = (sortOrder) => {
    setQuery((prev) => ({ ...prev, sortOrder }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  // FIN-11542 — shared async-export dispatcher. `range` carries the optional
  // offset/limit so the two dropdown options can scope the export:
  //   • Download Current Page → current page's offset/limit
  //   • Download All Matching → no range (service defaults to offset:0/limit:5000)
  // download:true + module='tm-activity' are the canonical async-flow markers;
  // BE persists a job row that surfaces in the shared Download History tab.
  // The export honours the active search / advanced-filter / sort state, same
  // as the listing fetch (mirrors the Tx tab's handleDownload).
  const dispatchActivityDownload = (range = {}) => {
    // FIN-12984 — the FRAML customer-detail adapter mounts this tab with alertId={undefined} by
    // design (FIN-12905/12906 made the tab customer-scoped), and isEmpty(undefined) is true, so
    // NEITHER dropdown option issued a request — the click did nothing at all. FIN-11398 relaxed
    // only the LISTING guard (:187) and left the download dispatcher and the pagination handler
    // on the single-alert guard.
    if ((!isEmpty(alertId) || isCustomerScope) && isVisible) {
      const { from, to, typologyId, isTriggeredOnly } = query;

      let triggeredOnly = hasTriggeredOnlySwitch ? isTriggeredOnly : undefined;

      const searchParams = buildSearchParams(query);
      const advancedFilterParams = buildAdvancedFilterParams(query);

      downloadAMLSTMCommonActivityInfo({
        customerId,
        alertId,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        // FIN-11542 Drift-2 — same precedence rule as the listing useEffect.
        isTriggeredOnly: requireTriggeredOnlyParam ? true : triggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        // FIN-12079 — variantId is a FRAML-P Advanced-Filter facet, stored under
        // query.advancedFilters.variantId and already flattened into the payload by
        // buildAdvancedFilterParams. The old flat `query.variantId` read was dead
        // (Activity never sets a top-level variantId) so the applied Variant never
        // reached the request / persisted download filter_params. Rely on advancedFilterParams.
        ...searchParams,
        ...advancedFilterParams,
        ...range,
        isSmartAssessment,
        download: true,
        // FIN-13392 — was hardcoded 'tm-activity', which misfiled every FRAML customer-detail
        // export under the generic TM_ACTIVITY module regardless of context. Now driven by the
        // `module` prop (default unchanged) so a caller can tag its own async-download module.
        module,
        // FIN-12039 — carry the alert's source id as moduleId so the async-download
        // job is enqueued with the correct sourceId (mirrors the Transaction
        // Information tab, TransactionDetails.js). Without it the BE defaults
        // sourceId to 0, so the completion notification is created but never
        // surfaces in the user's bell (delivery is keyed by the alert source id).
        moduleId: alertInfo?.alertSourceId
          ? Number(alertInfo.alertSourceId)
          : undefined,
        // FIN-13443 — fire the Download History refresh on submission SUCCESS
        // (saga strips + invokes it), not at dispatch, so the refetch cannot
        // run before the BE has written the job row.
        onSuccess: onDownloadComplete
      });
    }
  };

  // Download Current Page — scope the export to the rows currently shown.
  const onDownloadCurrentPage = () =>
    dispatchActivityDownload({
      offset: (currentPage - 1) * currentPageSize,
      limit: currentPageSize
    });

  // Download All Matching — no offset/limit so the BE queues an async job
  // (mirrors Transaction's All-Matching flow). downloadAll flags the saga to
  // show the queued "Export In Progress / Ready" toast.
  const onDownloadAll = () => dispatchActivityDownload({ downloadAll: true });

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
    const { from, to, isTriggeredOnly, typologyId } = query;
    // FIN-12984 — same relaxation on the pagination handler, broken for the same reason.
    if (from && to && (!isEmpty(alertId) || isCustomerScope) && isVisible) {
      // FIN-11542 Phase-C Defect-5 — carry the active search / advanced-filter
      // / sort state into the page-change refetch (mirrors the Tx tab's
      // onPaginationChange). Previously these params were dropped on paging.
      const wireTriggeredOnly = requireTriggeredOnlyParam
        ? true
        : hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;
      const searchParams = buildSearchParams(query);
      const advancedFilterParams = buildAdvancedFilterParams(query);

      getAMLSTMCommonActivityInfo({
        customerId,
        alertId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly: wireTriggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        // FIN-12079 — variantId is a FRAML-P Advanced-Filter facet, stored under
        // query.advancedFilters.variantId and already flattened into the payload by
        // buildAdvancedFilterParams. The old flat `query.variantId` read was dead
        // (Activity never sets a top-level variantId) so the applied Variant never
        // reached the request / persisted download filter_params. Rely on advancedFilterParams.
        ...searchParams,
        ...advancedFilterParams,
        isSmartAssessment
      });
    }
  };

  const typologyVariants = useMemo(
    () =>
      transactionTriggeredVariants
        ?.filter((t, i) => transactionTriggeredVariants.indexOf(t) === i)
        ?.map((d) => ({
          id: d,
          name: d
        })),
    [transactionTriggeredVariants]
  );

  const preparedRows = useMemo(() => {
    if (activityInfoData?.payload?.length) {
      return (activityInfoData?.payload || []).map((item) => ({
        ...item,
        ...(item.customData ? JSON.parse(item.customData) : {})
      }));
    }
    return [];
  }, [activityInfoData]);

  const COLUMNS = useMemo(() => {
    let customColumns = [...widgetConfig];

    // Helper to add a new column if not already present
    const addCustomColumn = (key, label, columnIndex) => {
      customColumns.push({
        columnIndex,
        columnId: key,
        columnName: label,
        columnVisibility: true,
        isDefault: true,
        columnDetail: [
          {
            itemIndex: columnIndex,
            itemLabel: label,
            itemName: key,
            visibility: true
          }
        ]
      });
    };

    // FIN-11542 — a customData key is "already rendered" when some configured
    // column already reads it via one of its columnDetail[].itemName data
    // accessors (dataIndex === itemName in genarateTableConfigs). The configured
    // columns use Title-Case columnId/columnName ("VPN Details") while the raw
    // customData keys are lowercase+underscore ("vpn_name"), so the old
    // `col.columnId === key` check never matched and appended duplicate columns.
    // Normalise (lowercase + non-alphanumeric→underscore) and compare against
    // itemName so configured columns suppress their raw-key twins; genuinely
    // unknown keys still get auto-added (that is the dynamic-column purpose).
    const normaliseKey = (k) =>
      String(k)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');
    // FIN-11542 D1 — a configured column "covers" a raw customData key when the
    // key's normalised form matches the column's data accessor (itemName) OR the
    // column's identity (columnId / columnName). The Activity widget config can
    // carry Title-Case columnName ("VPN Details") whose itemName is the raw key
    // ("vpn_name"); covering BOTH defends against either being the only match so
    // a configured column always suppresses its raw-key twin (no duplicate).
    const coveredKeys = new Set();
    customColumns.forEach((col) => {
      if (col?.columnId) coveredKeys.add(normaliseKey(col.columnId));
      if (col?.columnName) coveredKeys.add(normaliseKey(col.columnName));
      (col.columnDetail || []).forEach((item) => {
        if (item?.itemName) coveredKeys.add(normaliseKey(item.itemName));
      });
    });

    // FIN-11966 — schema-driven custom_json columns. Instead of sniffing every key
    // present in the rows' `custom_data` JSON (which was data-dependent and could not be
    // generalised, and never matched the download export), add one column per configured
    // activity `custom_json` custom field (from the `custom_field` table, `act_enabled=1`;
    // e.g. `channel` → "Channel"). The value is still read out of the row's `custom_data`
    // (spread into the row in `preparedRows`), so the header = `ui_name` and the accessor =
    // the field `name`. This is the same set of fields the BE export builds columns from,
    // so the tab and the "Download All Matching" file now match by configuration.
    const activityFieldDefs = queryFields?.fields?.activity || {};
    let columnIndex = customColumns.length;
    Object.entries(activityFieldDefs).forEach(([key, def]) => {
      if (
        def?.processingType === 'custom_json' &&
        !customColumns.some((col) => col.columnId === key) &&
        !coveredKeys.has(normaliseKey(key)) &&
        // FIN-13691 — skip a field that is null/empty on every loaded row (e.g. an
        // all-null Channel) so it does not render as a blank column. Per loaded page.
        preparedRows.some((row) => row[key] != null && row[key] !== '')
      ) {
        const label = def.label || titleCase(key);
        addCustomColumn(key, label, columnIndex);
        columnIndex++;
        coveredKeys.add(normaliseKey(key));
      }
    });

    const configs = result(ComponentsMap, mapNamespace) || {};
    return isSingleLevel
      ? genarateTableConfigs({ settings: customColumns, configs })
      : generateGroupedTableConfigs({ settings: customColumns, configs });
  }, [widgetConfig, isSingleLevel, queryFields, preparedRows]);

  return (
    <CustomActivityInfoPanel className={className}>
      {/* FIN-11542 fix: legacy react-csv direct-download anchor removed; the
          single async Download lives in the ActivityInfoFilters filter row
          (onDownloadClick → downloadAMLSTMCommonActivityInfo → Download
          History). */}
      {/* FIN-11542 — ActivityInfoFilters now renders its own two-row layout
          (SearchRow + MainFiltersRow), mirroring the Transaction Information
          tab. The previous TransactionDetalisPanelHeader wrapper used
          flex/space-between which laid the two rows side-by-side; rendering the
          filters directly lets the rows stack as intended. */}
      <ActivityInfoFilters
        hasTriggeredOnlySwitch={hasTriggeredOnlySwitch}
        hasTypologiesFilter={hasTypologiesFilter}
        typologies={isPrevention ? typologyVariants : typologies || []}
        showLabels={showWidgetActionLabels}
        filters={query}
        onFilterChange={onChangeWidgetAction}
        onSearchChange={onSearchChange}
        onAdvancedFiltersChange={onAdvancedFiltersChange}
        onSortChange={onSortChange}
        alertDateTO={alertDateTO}
        isPrevention={isPrevention}
        alertSourceId={alertSourceId}
        alertId={alertId}
        customerId={customerId}
        variantOptions={typologyVariants}
        /* FIN-11542 — 2-option Download dropdown (mock parity with the Tx tab)
           in the filter row next to the Sort dropdown. "Download Current Page"
           / "Download All Matching" each trigger the async export
           (downloadAMLSTMCommonActivityInfo → Download History). */
        onDownloadCurrentPage={
          hasDownloadButton ? onDownloadCurrentPage : undefined
        }
        onDownloadAll={hasDownloadButton ? onDownloadAll : undefined}
        isDownloading={isDownloadingActivity}
        isDownloadDisabled={(activityInfoData?.payload || []).length === 0}
        currentPageCount={(activityInfoData?.payload || []).length}
        totalCount={activityInfoData?.total || 0}
      />
      <TransactionDetailsTable
        dataSource={preparedRows}
        columns={COLUMNS}
        loading={isLoading}
        pagination={{
          total: activityInfoData?.total || 0,
          current: currentPage,
          onChange: onPaginationChange,
          pageSize: currentPageSize,
          // FIN-12073 — mirror the Results tab (PreventionResultTable):
          // expose the page-size dropdown and a record count. Ant v4 only
          // auto-renders the size changer when total > 50, so set it
          // explicitly to keep the [10/20/50/100] picker available for
          // smaller activity sets too.
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: true,
          showTotal: (total) => `${total} Records`
        }}
      />
    </CustomActivityInfoPanel>
  );
};

ActivityInformation.propTypes = {
  apiActions: PropTypes.object.isRequired,
  alertId: PropTypes.string,
  isLoading: PropTypes.bool,
  typologies: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  hasDownloadButton: PropTypes.bool,
  showWidgetActionLabels: PropTypes.bool,
  className: PropTypes.string,
  hasTypologiesFilter: PropTypes.bool,
  alertDetails: PropTypes.object,
  manualCaseDate: PropTypes.string,
  activityInfoData: PropTypes.object,
  mapNamespace: PropTypes.string,
  settings: PropTypes.object,
  gsQuery: PropTypes.object,
  isPrevention: PropTypes.bool,
  hasTriggeredOnlySwitch: PropTypes.bool,
  requireTriggeredOnlyParam: PropTypes.bool,
  transactionTriggeredVariants: PropTypes.array,
  isSmartAssessment: PropTypes.bool,
  onDownloadComplete: PropTypes.func,
  isDownloadingActivity: PropTypes.bool,
  alertSourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCustomerScope: PropTypes.bool,
  module: PropTypes.string
};

const CustomActivityInfoPanel = styled(TransactionDetalisPanel)`
  padding: 10px 20px 20px;
`;

export default ActivityInformation;
